<template>
  <div
    class="h-7 bg-[#313131] items-center flex text-[10px] opacity-70 relative"
  >
    <!-- 包含内存和功耗信息的容器 -->
    <div class="ml-3 flex">
      <!-- 显示图片占用固件内存大小 -->
      <div v-if="maxRamLimit">
        图片占用固件内存大小：
        <a-tooltip>
          <template #title>图片总大小请不要超过限定值</template>
          <span
            :class="{
              'text-[#ff0000]':
                maxRamLimit && (maxRamTheme?.ram ?? 0) > maxRamLimit,
              ' hover:underline': isDev
            }"
            @click="() => isDev && openRam()"
          >
            {{ maxRamTheme?.ram ?? '0' }}KB / {{ maxRamLimit }}KB
          </span>
        </a-tooltip>
      </div>
      <!-- 功耗 -->
      <!-- 如果支持功耗等级显示，则显示功耗等级信息 -->
      <template v-if="supportPowerClass">
        <!-- 分隔线 -->
        <div
          v-if="maxRamLimit"
          class="border-l-[0.7px] border-l-[#ffffff]/30 border-solid h-4 mx-2"
        />
        <!-- 点击可打开功耗等级模态框 -->
        <div
          class="hover:border-b border-b-[#ffffff]/30 hover:cursor-pointer"
          @click="openPowerClass"
        >
          表盘功耗等级：{{ watchfacePowerData?.powerClass ?? '-' }}级
        </div>
      </template>

      <!-- 添加一个表盘息屏校验的div -->
      <div
        v-if="canuseAODValid"
        class="ml-3 hover:underline hover:cursor-pointer"
        @click="openAodModal"
      >
        <span class="mr-1"> 息屏校验 </span>
      </div>
    </div>

    <!-- 显示表盘名称、设备类型和大小的容器 -->
    <div
      class="absolute right-0 border-b border-b-transparent hover:border-b-[#ffffff]/30 mr-2.5 mt-[1px]"
      @click="editWatchface"
    >
      <!-- 显示表盘名称 -->
      <span class="mr-1"> {{ description.name }} </span>
      <!-- 显示设备类型 -->
      <span class="mr-1">
        {{ getProductNameIfNeed(description.deviceType ?? 'N62') }}
      </span>
      <!-- 显示表盘设备大小 -->
      <span> {{ watchfaceStore.watchfaceDevice?.size }} </span>
    </div>
  </div>
  <modal-watchface-power-class ref="watchfacePowerClassModal" />
  <modal-watchface-ram ref="watchfaceRamModal" />
  <modal-watchface-aod ref="watchfaceAodModal" />
</template>

<script setup lang="ts">
import {
  useEmitterListener,
  useImageAutoFormat,
  useWatchfaceAodPixel,
  useWatchfaceDevices,
  useWatchfacePowerClass,
  useWatchfaceRam,
  watchfaceImageLoading,
  watchfaceOpening
} from '@/hooks';
import { useWatchfaceStore } from '@/store/use-watchface-store';
import { canUse } from '@/utils/can-use.utils';
import { debounce } from 'lodash-es';
import { computed, onBeforeUnmount, onDeactivated, ref } from 'vue';
import { GlobalConfig } from '@/services/gloabl.config';
import { EventType, emitter } from '@/utils/emitter.utils';
import { useRoute } from 'vue-router';
import { KonvaDrag } from '@/services/konva/konva.drag';

const watchfaceStore = useWatchfaceStore();
const description = computed(() => watchfaceStore.description);
const { getProductNameIfNeed } = useWatchfaceDevices();
const { resetImgsFormat } = useImageAutoFormat();

const editWatchface = () => {
  emitter.emit(EventType['modal:watchface-manage'], {
    path: watchfaceStore.path,
    mode: 'edit'
  });
};

const watchfacePowerClassModal = ref();
const openPowerClass = () => {
  watchfacePowerClassModal.value.open();
};

const route = useRoute();

// 内存值
const { recalculateThemesRam, maxRamTheme, maxRamLimit } = useWatchfaceRam();
const { resetWatchfacePowerData, watchfacePowerData } =
  useWatchfacePowerClass();

const _reset = debounce(async () => {
  if (route.name != 'editor') return;

  // 功耗
  if (supportPowerClass.value) {
    resetWatchfacePowerData();
  }

  // 如果需要计算内存，则需要考虑先自动选择格式，否则无需考虑
  if (GlobalConfig.autoChooseImageFormat && GlobalConfig.validWatchfaceRam) {
    console.time('resetImgsFormat');
    await resetImgsFormat();
    console.timeEnd('resetImgsFormat');
  }
  // 内存
  if (GlobalConfig.validWatchfaceRam) {
    console.time('recalculateThemesRam');
    recalculateThemesRam();
    console.timeEnd('recalculateThemesRam');
  }
}, 200);

useEmitterListener(EventType['watchface:opened-resources'], _reset);
useEmitterListener(EventType['maml:rendered'], (data: { reason: string }) => {
  const { reason } = data ?? {};
  if (
    [
      'theme-changed',
      'config-data-changed',
      'canvas-scale',
      'keep-data'
    ].includes(reason)
  ) {
    return;
  }
  if (watchfaceImageLoading.value) return;
  if (watchfaceOpening.value) return;
  if (route.name != 'editor') return;
  if (KonvaDrag.DRAGGING) return;
  _reset();
});

// 功耗
const supportPowerClass = computed(() => canUse('power-class').valid);

onBeforeUnmount(() => {
  watchfacePowerData.value = undefined;
});
onDeactivated(() => {
  watchfacePowerData.value = undefined;
});

// 内存
const watchfaceRamModal = ref();
const isDev = import.meta.env.DEV;
const openRam = () => {
  watchfaceRamModal.value?.open?.();
};

// 息屏校验
const { canuseAODValid } = useWatchfaceAodPixel();
// 添加是否可以aod的的判断
const watchfaceAodModal = ref();
const openAodModal = () => {
  watchfaceAodModal.value?.open?.();
};
</script>

<style scoped></style>
